/**
 * The browser's half of the POS slice.
 *
 * Two calls, both same-origin, both typed by `@planforge/api/wire` rather than by a
 * second declaration of the same shapes here. That import is type-only and erased at
 * build time, so nothing of the edge functions ships in the bundle — what crosses is
 * the agreement about field names, which is exactly the thing that drifts silently
 * when it is written down twice (DESIGN §4).
 *
 * Nothing Stripe-shaped is in this file, and there is no publishable key in the app at
 * all. The milestone budgeted for one; it turned out not to be needed, because a
 * server that has already created the session has the URL to send the shopper to, and
 * the key exists to let a client construct that URL for itself.
 */

import type { CheckoutStartResponse, PurchaseResponse } from "@planforge/api/wire";

/** Same origin as the app: the API Worker answers the site's own `/api/*` route. */
const API = "/api";

/**
 * A refusal from the edge, or from the network, as one thing the UI can show.
 *
 * `code` is the stable half — the poll branches on it to wait through a transient
 * refusal, and the screen shows `message` — which is the same split the engine's
 * `Diagnostic` makes and for the same reason.
 *
 * `explained` is whether the message is worth a customer's time. A 4xx with a body is
 * the edge saying what was wrong with *this* request ("too many checkouts from here"),
 * written for the person who made it. Anything else — a 5xx, a gateway's HTML page, a
 * network that is not there — says only that our side did not answer, and the screen
 * that asked knows how to say that in its own terms far better than a status code
 * does. "The server answered 502." was the whole of what a buyer saw when the edge was
 * down; the screen's own sentence says what it means for them (nothing was charged,
 * the note is still here).
 */
export class ApiFailure extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly explained = false,
  ) {
    super(message);
    this.name = "ApiFailure";
  }
}

/** What an unanswered request is called, whatever was in the way. */
export const UNREACHABLE = "unreachable";

/** Reads a non-2xx answer from the edge into one `ApiFailure`. */
export async function refusal(response: Response): Promise<ApiFailure> {
  let body: { error?: { code?: string; message?: string } } | undefined;
  try {
    body = (await response.json()) as typeof body;
  } catch {
    body = undefined;
  }
  const code = body?.error?.code;
  const message = body?.error?.message;
  if (code === undefined || message === undefined) {
    // Not the edge's own refusal: a proxy's error page, or a platform that could not
    // start the function. Either way nothing of ours answered.
    return new ApiFailure(UNREACHABLE, "Our server could not be reached.");
  }
  return new ApiFailure(code, message, response.status < 500);
}

/**
 * `fetch`, with a network failure turned into the same `ApiFailure` a gateway's 502
 * is. To a customer they are one thing — we did not answer — and a `TypeError: Failed
 * to fetch` reaching a screen is a stack trace in a sentence's place.
 */
export async function ask(url: string, init?: RequestInit): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(url, init);
  } catch {
    throw new ApiFailure(UNREACHABLE, "Our server could not be reached.");
  }
  if (!response.ok) {
    throw await refusal(response);
  }
  return response;
}

/**
 * The sentence a screen shows for a failed call: the edge's own words when it
 * explained a refusal of this request, and otherwise the screen's sentence for "we
 * did not answer", which is the only one that can say what that means for this
 * customer here.
 */
export function plainly(reason: unknown, unanswered: string): string {
  return reason instanceof ApiFailure && reason.explained ? reason.message : unanswered;
}

/** What the buy button says when checkout could not be started. No money has moved. */
export const CHECKOUT_UNREACHABLE =
  "Checkout couldn't be reached just now. Nothing was charged, and your design is " +
  "just as you left it — try again in a minute.";

/**
 * Asks the edge for a Stripe Checkout session and hands back where to send the
 * shopper, and the session's id to file the design under.
 *
 * Nothing is sent. The design never leaves this browser — it is built into the plan
 * here on the way back — and the edge stores nothing about a purchase (DESIGN §9).
 */
export async function startCheckout(): Promise<CheckoutStartResponse> {
  const response = await ask(`${API}/create-checkout`, { method: "POST" });
  return (await response.json()) as CheckoutStartResponse;
}

/**
 * Has this checkout been paid? The edge asks Stripe and answers.
 *
 * `pending` is an ordinary answer and not an error, so the caller polls on it rather
 * than treating it as a failure.
 */
export async function fetchPurchase(checkoutId: string): Promise<PurchaseResponse> {
  const response = await ask(`${API}/purchase?checkout=${encodeURIComponent(checkoutId)}`);
  return (await response.json()) as PurchaseResponse;
}

/** How long to wait between asks, and how long to keep asking before saying so. */
export const POLL_INTERVAL_MS = 1500;
export const POLL_TIMEOUT_MS = 90_000;

/**
 * Refusals worth asking again after: Stripe having a moment, the rate limit, or our
 * side not answering at all. All of them pass, and none says anything about whether
 * the customer paid.
 */
const TRANSIENT = new Set(["stripe_unavailable", "busy", "unavailable", UNREACHABLE]);

/**
 * Polls until the purchase is confirmed, the deadline passes, or the caller gives up.
 *
 * A deadline rather than a retry count, because what a shopper is actually waiting on
 * is a confirmation that usually comes at once and occasionally takes a while. When it
 * runs out the answer is "still confirming" with a button — never "your purchase
 * failed", which would be a lie about money that has definitely moved. A transient
 * refusal along the way is waited through, not reported; one that persists to the
 * deadline is thrown, so the screen can say what it is.
 */
export async function awaitPurchase(
  checkoutId: string,
  signal?: AbortSignal,
): Promise<PurchaseResponse> {
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  for (;;) {
    if (signal?.aborted === true) {
      return { status: "pending" };
    }
    let answer: PurchaseResponse | undefined;
    try {
      answer = await fetchPurchase(checkoutId);
    } catch (reason) {
      if (!(reason instanceof ApiFailure && TRANSIENT.has(reason.code)) || Date.now() >= deadline) {
        throw reason;
      }
    }
    if (answer?.status === "ready" || (answer !== undefined && Date.now() >= deadline)) {
      return answer;
    }
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
}
